import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { NgForm, ValidationErrors } from '@angular/forms'
import { AppConfigService } from 'app/shared/services/app-config.service'
import { MessageBusService } from 'app/shared/services/message-bus.service'
import { catchError, Observable, of, tap, throwError } from 'rxjs'
import { ApiErrorMessageResponse, ApiMessageResponse, ApiResponse, HttpVerbConstants } from '../generated/generated'
import { ApiValidationErrors, HandledError } from '../models/handled-error'
import { UIMesssageConstants } from './../generated/generated'

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  public constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService,
    private messageBusService: MessageBusService
  ) {}

  public form: NgForm

  private createApiUrl(endpoint: string): string {
    return this.appConfigService.getApiUrlWithEndpoint(endpoint)
  }

  public execute<T>(endpoint: string, method: string, body?: any, form?: NgForm): Observable<ApiResponse<T>> {
    if (form != null) {
      this.form = form
    }

    let observable = new Observable<ApiResponse<T>>()

    switch (method) {
      case HttpVerbConstants.get: {
        observable = this.get<T>(endpoint)
        break
      }
      case HttpVerbConstants.post: {
        observable = this.post<T>(endpoint, body)
        break
      }
      case HttpVerbConstants.put: {
        observable = this.put<T>(endpoint, body)
        break
      }
      case HttpVerbConstants.delete: {
        observable = this.delete<T>(endpoint)
        break
      }
      default: {
        break
      }
    }

    if (this.form != null) {
      observable = observable.pipe(
        catchError((error) => {
          {
            if ((error as unknown as HandledError)?.hasAlreadyBeenHandled) {
              if (error as unknown as ApiValidationErrors) {
                let validationErrors = error as unknown as ApiValidationErrors
                this.addFormErrorHandling(validationErrors, this.form)
                return of()
              }
              return throwError(() => new Error(error))
            }
          }
          return throwError(() => new Error(error))
        })
      )
    }

    observable = observable.pipe(
      tap((data) => {
        if (!data?.succeeded) {
          this.addErrorToast(data.errors)
        } else {
          this.addSuccessToast(data.result)
        }
      })
    )

    return observable
  }

  public get<T>(endpoint: string): Observable<ApiResponse<T>> {
    const url = this.createApiUrl(endpoint)
    return this.http.get<ApiResponse<T>>(url)
  }

  public post<T>(endpoint: string, body: any): Observable<ApiResponse<T>> {
    const url = this.createApiUrl(endpoint)
    return this.http.post<ApiResponse<T>>(url, body)
  }

  public put<T>(endpoint: string, body: any): Observable<ApiResponse<T>> {
    const url = this.createApiUrl(endpoint)
    return this.http.put<ApiResponse<T>>(url, body)
  }

  public delete<T>(endpoint: string): Observable<ApiResponse<T>> {
    const url = this.createApiUrl(endpoint)
    return this.http.delete<ApiResponse<T>>(url)
  }

  private addSuccessToast(dataResult: any) {
    let isApiMessageResponse =
      (<ApiMessageResponse>dataResult)?.message !== null &&
      (<ApiMessageResponse>dataResult)?.type === 'ApiMessageResponse'

    if (isApiMessageResponse) {
      let message = ''
      message = (<ApiMessageResponse>dataResult).message
      this.messageBusService.addSuccessToast(UIMesssageConstants.genericSuccess, message)
    }
  }

  private addErrorToast(errors: Array<ApiErrorMessageResponse>) {
    let isApiMessageResponse =
      errors &&
      errors.length > 0 &&
      (<ApiErrorMessageResponse>errors[0]).message !== null &&
      (<ApiErrorMessageResponse>errors[0]).type === 'ApiErrorMessageResponse'

    if (isApiMessageResponse) {
      let message = ''
      if (errors) {
        if (errors.length == 1) {
          message = errors[0].message
        } else {
          message = errors
            .map((errorMessage, index) => {
              return `<li>${errors[index].message}</li>`
            })
            .join('')
        }
      }

      this.messageBusService.addErrorToast(UIMesssageConstants.genericFailure, message)
    }
  }

  private addFormErrorHandling(apiValidationErrors: ApiValidationErrors, form: NgForm) {
    if (apiValidationErrors?.validationErrors) {
      apiValidationErrors.validationErrors.forEach((message) => {
        let controlName = message.fieldName.replace(/^[A-Z]./, ($1) => $1.toLowerCase())
        const control = form.controls[controlName]
        if (control != null) {
          let validationMap: ValidationErrors = new Object()
          validationMap['value'] = message.validationMessage
          control.setErrors(validationMap)
        }
      })
    }
  }
}
